/**
 * Card filter for the Statements page.
 *
 * With one card a list is fine; with eight and 47 statements it isn't — you
 * can't answer "what did Swiggy bill me in April?" by scrolling. This is the
 * primary axis people actually think in: *which card*.
 *
 * Rendered as chips rather than a dropdown deliberately. The set is small and
 * stable, each chip carries its own count and flag state, and one click both
 * filters and tells you how much is there — a dropdown hides all of that
 * behind an interaction.
 */

import { Badge } from "@/components/ui/primitives";
import { cn } from "@/lib/cn";
import type { Card, Statement } from "@/lib/api/client";

interface Props {
  cards: Card[];
  statements: Statement[];
  selected: string | null;
  onSelect: (cardId: string | null) => void;
}

export function CardFilterBar({ cards, statements, selected, onSelect }: Props) {
  const countFor = (cardId: string) => statements.filter((s) => s.card_id === cardId).length;
  const flaggedFor = (cardId: string) =>
    statements.filter((s) => s.card_id === cardId && s.needs_review).length;

  // Cards with no statements yet still belong here — their absence is
  // information ("nothing has arrived for this one"), not a reason to hide.
  const ordered = [...cards].sort((a, b) => countFor(b.id) - countFor(a.id));
  const totalFlagged = statements.filter((s) => s.needs_review).length;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 max-md:-mx-4 max-md:flex-nowrap max-md:overflow-x-auto max-md:px-4 max-md:pb-1 max-md:[scrollbar-width:none] max-md:[&>*]:shrink-0"
      role="group"
      aria-label="Filter by card"
    >
      <Chip
        active={selected === null}
        onClick={() => onSelect(null)}
        label="All cards"
        count={statements.length}
        flagged={totalFlagged}
      />
      {ordered.map((card) => (
        <Chip
          key={card.id}
          active={selected === card.id}
          onClick={() => onSelect(selected === card.id ? null : card.id)}
          label={card.nickname || `${card.issuer} ····${card.last4}`}
          sublabel={`····${card.last4}`}
          count={countFor(card.id)}
          flagged={flaggedFor(card.id)}
          theme={card.theme}
        />
      ))}
    </div>
  );
}

function Chip({
  active,
  onClick,
  label,
  sublabel,
  count,
  flagged,
  theme,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  sublabel?: string;
  count: number;
  flagged: number;
  theme?: Card["theme"];
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={sublabel ? `${label} ${sublabel}` : label}
      className={cn(
        "group inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors",
        active
          ? "border-brass/50 bg-brass/10 text-ink"
          : "border-hairline bg-panel text-muted hover:border-hairline-strong hover:text-ink",
      )}
    >
      {/* A sliver of the card's own colour — ties the chip to the Card Wall
          without spending the brass accent on navigation. */}
      {theme && (
        <span
          aria-hidden
          className="h-3.5 w-1 shrink-0 rounded-full"
          style={{
            background: `linear-gradient(${theme.gradient_from}, ${theme.gradient_to})`,
          }}
        />
      )}
      <span className="max-w-[11rem] truncate font-medium">{label}</span>
      <span className={cn("tabular-nums", active ? "text-brass" : "text-faint")}>{count}</span>
      {flagged > 0 && (
        <Badge tone="warn" className="px-1 py-0">
          {flagged}
        </Badge>
      )}
    </button>
  );
}
